/**
 * ReplayControls Component
 *
 * Mode toggle (Steps/Agent), task text (Agent mode), and action button.
 * Source toggle (Cached/Live/Hybrid) is in the top bar above the stream.
 */

import React from 'react';

interface ReplayControlsProps {
  mode: 'steps' | 'agent';
  isRunning: boolean;
  isStopping?: boolean;
  taskText: string;
  onModeChange: (mode: 'steps' | 'agent') => void;
  onReplay: () => void;
  onStop: () => void;
  onTaskTextChange: (text: string) => void;
  disabled: boolean;
}

export const ReplayControls: React.FC<ReplayControlsProps> = ({
  mode,
  isRunning,
  isStopping = false,
  taskText,
  onModeChange,
  onReplay,
  onStop,
  onTaskTextChange,
  disabled,
}) => {
  const modeOptions: Array<{ value: 'steps' | 'agent'; label: string }> = [
    { value: 'steps', label: 'Steps' },
    { value: 'agent', label: 'Agent' },
  ];

  return (
    <div className="space-y-3">
      {/* Mode Toggle */}
      <div>
        <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
          {modeOptions.map((opt) => (
            <button
              key={opt.value}
              onClick={() => onModeChange(opt.value)}
              disabled={disabled || isRunning}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors flex-1 ${
                mode === opt.value
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Agent mode: Task text */}
      {mode === 'agent' && (
        <div>
          <textarea
            value={taskText}
            onChange={(e) => onTaskTextChange(e.target.value)}
            placeholder="Describe what the agent should do..."
            disabled={disabled || isRunning}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 resize-none disabled:bg-gray-100 disabled:text-gray-600 disabled:cursor-not-allowed"
            rows={3}
          />
        </div>
      )}

      {/* Action Button */}
      {isRunning ? (
        <button
          onClick={onStop}
          disabled={isStopping}
          className="w-full px-4 py-2 text-sm font-medium rounded-md bg-red-600 text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isStopping ? 'Stopping...' : '\u25A0 Stop'}
        </button>
      ) : (
        <button
          onClick={onReplay}
          disabled={disabled}
          className={`w-full px-4 py-2 text-sm font-medium rounded-md text-white focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
            mode === 'steps'
              ? 'bg-green-600 hover:bg-green-700 focus:ring-green-500'
              : 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500'
          }`}
        >
          &#9654; {mode === 'steps' ? 'Replay' : 'Run'}
        </button>
      )}
    </div>
  );
};
